import { Fragment, useState } from "react"
import {
	Alert,
	Button,
	Dropdown,
	Icons,
	Inline,
	Money,
	Stack,
	Table,
	Text,
} from "@sembark-travel/ui-base"
import {
	ConfirmationDialog,
	ConfirmationDialogProvider,
	useDialog,
} from "@sembark-travel/ui-dialog"
import { ListView, Search, useSearch } from "@sembark-travel/ui-list"
import { useXHR } from "@sembark-travel/xhr"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import {
	addPercentageToMoney,
	moneyParseByDecimal,
} from "@sembark-travel/money"
import { PERMISSIONS, useCheckPermissions } from "../Auth"
import { IHotel } from "../Hotels"
import { Thumbnail } from "../shared/Thumbnail"
import { CodeChip } from "../HotelOtaMealPlans"
import { hotelAddOnsXHR, THotelAddon } from "./store"
import { HotelAddOnInDialog } from "./NewItem"
import { hotelAddOnFormValuesToFormData, hotelAddOnToFormValues } from "./utils"

export function HotelAddOns({
	hotel,
	onChange,
}: {
	hotel: IHotel
	onChange: () => void
}) {
	const [params, setParams] = useSearch<Record<string, unknown>>()
	const { hasPermission } = useCheckPermissions()
	const canManage = hasPermission(PERMISSIONS.CREATE_HOTELS)
	const xhr = useXHR()
	const [isAddOpen, openAddDialog, closeAddDialog] = useDialog()
	const [freshKey, setFreshKey] = useState(1)
	function handleChange() {
		setFreshKey((prev) => prev + 1)
		onChange()
	}
	return (
		<Fragment>
			<Search
				initialParams={params}
				onSearch={(newParams) => setParams({ ...newParams, page: 1 })}
				title="Extra Services"
				placeholder="Search"
				actions={
					canManage ? (
						<Button level="primary" onClick={openAddDialog}>
							Add Extra Service
						</Button>
					) : null
				}
			>
				<ListView<THotelAddon>
					pageKey={`hotel-${hotel.id}-add-ons-${freshKey}`}
					fetch={(xhr, params) =>
						hotelAddOnsXHR(xhr).getAddOns(hotel.id, params)
					}
					params={params}
					onPageChange={(page) => setParams({ ...params, page })}
				>
					{({ items }) => (
						<Table
							bordered
							responsive
							headers={["Name", "Total Rate", "Tax", ""]}
							rows={items.map((addOn) => [
								<Inline gap="4" alignItems="center">
									<Thumbnail
										imageUrl={addOn.thumb_image_url}
										size="10"
										fallbackIcon={<Icons.Utensils size="4" color="primary" />}
									/>
									<Stack gap="1">
										<Inline gap="2" alignItems="center">
											<Text fontWeight="semibold">{addOn.name}</Text>
											<CodeChip code={addOn.code} />
											{addOn.is_service_date_flexible ? (
												<Inline alignItems="center" color="muted" gap="1">
													<Icons.Info size="4" />
													<Text as="i" fontSize="sm">
														Subject to availability
													</Text>
												</Inline>
											) : null}
										</Inline>
										{addOn.description ? (
											<Text fontSize="sm" color="muted">
												{addOn.description}
											</Text>
										) : null}
									</Stack>
								</Inline>,
								<Money
									money={addPercentageToMoney(
										moneyParseByDecimal(
											Number(addOn.rate_before_tax),
											hotel.currency
										),
										Number(addOn.tax_percentage)
									)}
									showCurrency
									whiteSpace="nowrap"
								/>,
								`${Number(addOn.tax_percentage)}%`,
								canManage ? (
									<AddOnRowActions
										hotel={hotel}
										addOn={addOn}
										onChange={handleChange}
									/>
								) : null,
							])}
						/>
					)}
				</ListView>
			</Search>
			{canManage ? (
				<HotelAddOnInDialog
					currency={hotel.currency}
					open={isAddOpen}
					onClose={closeAddDialog}
					onSuccess={async (values) => {
						await hotelAddOnsXHR(xhr).createAddOn(
							hotel.id,
							hotelAddOnFormValuesToFormData(values)
						)
						showSnackbar("Extra service added successfully")
						closeAddDialog()
						handleChange()
					}}
				/>
			) : null}
		</Fragment>
	)
}

function AddOnRowActions({
	hotel,
	addOn,
	onChange,
}: {
	hotel: IHotel
	addOn: THotelAddon
	onChange: () => void
}) {
	const xhr = useXHR()
	const [isEditOpen, showEdit, hideEdit] = useDialog()
	return (
		<>
			<RowActionsMenu
				itemType="Extra Service"
				itemName={addOn.name}
				onEdit={showEdit}
				onDelete={async () => {
					try {
						const { message } = await hotelAddOnsXHR(xhr).deleteAddOn(addOn.id)
						showSnackbar(message)
						onChange()
					} catch (e) {
						showSnackbar(
							(e as Error).message || "Something went wrong. Please try later."
						)
					}
				}}
			/>
			<HotelAddOnInDialog
				initialValues={hotelAddOnToFormValues(addOn)}
				initialImageUrl={addOn.image_url}
				currency={hotel.currency}
				open={isEditOpen}
				onClose={hideEdit}
				onSuccess={async (values) => {
					await hotelAddOnsXHR(xhr).updateAddOn(
						hotel.id,
						addOn.id,
						hotelAddOnFormValuesToFormData(values)
					)
					showSnackbar("Extra service updated successfully")
					hideEdit()
					onChange()
				}}
			/>
		</>
	)
}

function RowActionsMenu({
	itemType,
	itemName,
	onEdit,
	onDelete,
}: {
	itemType: string
	itemName: string
	onEdit: () => void
	onDelete: () => void | Promise<void>
}) {
	return (
		<Inline gap="2" justifyContent="end">
			<Button
				size="sm"
				level="tertiary"
				onClick={onEdit}
				title={`Edit ${itemType}`}
			>
				<Icons.Pencil />
			</Button>
			<ConfirmationDialogProvider>
				{({ confirm }) => (
					<>
						<Dropdown alignRight>
							<Dropdown.ToggleButton size="sm" level="tertiary">
								<Icons.DotsVertical />
							</Dropdown.ToggleButton>
							<Dropdown.Menu>
								<Dropdown.MenuItem
									color="danger"
									onClick={() => {
										confirm().then((yes) => {
											if (!yes) return
											return onDelete()
										})
									}}
								>
									<Icons.Ban /> Delete {itemType}
								</Dropdown.MenuItem>
							</Dropdown.Menu>
						</Dropdown>
						<ConfirmationDialog
							title={`Delete ${itemType}`}
							destructiveAction
							acceptActionLabel="Yes, remove"
						>
							<Alert status="error">
								Are you sure you want to Delete{" "}
								<Text as="span" fontWeight="semibold">
									{itemName}
								</Text>
								?
							</Alert>
						</ConfirmationDialog>
					</>
				)}
			</ConfirmationDialogProvider>
		</Inline>
	)
}
